import {bindable, bindingMode, customElement, inject, observable,} from "aurelia-framework";
import {EventAggregator} from "aurelia-event-aggregator";
import {Client} from "../api/client";
import getPropertyByPath from "../utilities/get-property-by-path";
import {I18N} from "aurelia-i18n";

@customElement("sio-settings")
@inject(Client, EventAggregator, I18N)
export class Settings {
    @bindable object;
    @bindable({defaultBindingMode: bindingMode.twoWay}) searchValue;

    @observable searchValue = "";
    loading = true;

    constructor(client, ea, i18n) {
        this.client = client;
        this.ea = ea;
        this.i18n = i18n;
    }

    async bind() {
        this.loading = true;

        const config = await this.client.get(
            this.object.modelId + "/" + this.object.id + "/settings"
        );

        this.panels = (config.panels || []).sort(
            (a, b) => b.priority - a.priority
        );

        const usedTabs = [];

        this.panels.forEach((panel) => {
            panel.object = getPropertyByPath(
                this.object,
                panel.options.property
            ) || {modelId: panel.options.modelId};

            panel.actionContext = {
                id: this.object.id,
                modelId: this.object.modelId,
            };
            usedTabs.push(panel.tab);
        });

        this.tabs = (config.tabs || [])
            .filter((tab) => usedTabs.includes(tab.id) || tab?.preset?.type === 'table')
            .sort((a, b) => a.weight - b.weight);
        this.defaultTabs = this.tabs;
        this.defaultPanels = this.panels;
        if (config.tabs.length) {
            const id = sessionStorage.getItem("sio_settings_tab");

            this.selectTab(
                config.tabs.find((tab) => tab.id === id) || config.tabs[0]
            );
        }

        this.loading = false;
    }

    attached() {
        this.subscription = this.ea.subscribe(
            "sio_form_post_submit",
            (event) => {
                if (
                    event.config &&
                    this.object.modelId === event.config.modelId
                ) {
                    this.loading = true;
                    this.object = this.client
                        .get(this.object.modelId + "/" + this.object.id)
                        .then((object) => {
                            this.object = object;
                            return this.bind();
                        });
                }
            }
        );
    }

    detached() {
        if (this.subscription) {
            this.subscription.dispose();
            this.subscription = null;
        }
    }

    selectTab(tab) {
        if (this.searchValue !== "") {
            this.resetDisplayTabs(
                this.filterPanelsByField(this.panels, this.searchValue),
                this.defaultTabs,
                tab
            );
        } else {
            this.activeTab = tab;
            this.leftColumn = this.panels.filter(
                (p) => p.tab === tab.id && "right" !== p.column
            );
            this.rightColumn = this.panels.filter(
                (p) => p.tab === tab.id && "right" === p.column
            );
        }
        sessionStorage.setItem("sio_settings_tab", tab.id);
    }

    filterPanelsByField(panels, keyword) {
        const filteredPanels = [];
        for (const panel of panels) {
            const panelLabel = this.i18n.tr(panel.title).toLowerCase();
            const keywordExistsInPanel = panelLabel.includes(
                keyword.toLowerCase()
            );
            if (keywordExistsInPanel) {
                filteredPanels.push(panel);
            } else {
                const filteredFields = panel.fields.filter((field) =>
                    field?.label?.toLowerCase().includes(keyword.toLowerCase())
                );
                if (filteredFields.length > 0) {
                    const filteredPanel = {...panel, fields: filteredFields};
                    filteredPanels.push(filteredPanel);
                }
            }
        }
        return filteredPanels;
    }

    resetDisplayTabs(panels, tabs, activeTab = null) {
        this.tabs = tabs.filter((tab) => {

            if ((this.searchValue == '' || this.searchValue == null) && tab?.preset?.type === 'table') {
                return true;
            }

            return panels.some((d) => d.tab == tab?.id) ||
                (tab?.preset?.type === 'table' && this.i18n.tr(tab.heading).toLowerCase().includes((this.searchValue ?? '').toLowerCase()))
                ;
        });
        if (
            !activeTab &&
            !this.tabs.some((tab) => tab?.id === this.activeTab?.id)
        ) {
            this.activeTab = this.tabs[0];
        }
        if (activeTab) {
            this.activeTab = activeTab;
            sessionStorage.setItem("sio_settings_tab", this.activeTab.id);
        }
        this.leftColumn = panels.filter(
            (p) => p.tab === this.activeTab.id && "right" !== p.column
        );
        this.rightColumn = panels.filter(
            (p) => p.tab === this.activeTab.id && "right" === p.column
        );
    }

    searchValueChanged(newValue, oldValue) {
        if (newValue !== "") {
            const filteredPanels = this.filterPanelsByField(
                this.panels,
                newValue
            );
            this.resetDisplayTabs(filteredPanels, this.defaultTabs);
        }
        if (!newValue) {
            this.resetDisplayTabs(this.defaultPanels, this.defaultTabs);
        }
    }

    additionalFetchConditions(tab, object) {
        const refField = tab.preset.properties.refField;
        const presetConditions = tab.preset.conditions;

        let conditions = {};

        if (Array.isArray(refField)) {
            conditions = refField.map(function (e) {
                return {
                    [e]: {$eq: {id: object.id, modelId: object.modelId}}
                };
            });

            return {
                $or: conditions
            }
        } else if (refField) {
            conditions = {
                [refField]: {$eq: {id: object.id, modelId: object.modelId}}
            };
        }

        if (presetConditions && Object.keys(presetConditions).length > 0) {
            conditions = {$and: [conditions, presetConditions]};
        }

        return conditions;
    }
}
